import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { Radio } from '@creditornot/cb-radio';
import { Select } from '@creditornot/cb-select';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useContext, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { bulkEditItems } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { BulkEditPayload, Category, MenuContainer } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper, WoltModal } from 'common/components';
import InputNumber from 'common/components/InputNumber';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import validateYupSchema from 'common/utils/validateYupSchema';
import { reportBulkAction } from 'modules/menu/behavioral';
import { useBulkChangePriceTracking } from 'modules/menu/hooks/telemetryHooks';
import InputMonetary from 'modules/menu/items/components/ItemDetailsForm/InputMonetary';
import useMenuData from 'modules/menu/useMenuData';

import { buildBulkActionUpdatePricePayload } from './fromPriceChangeFormValuesToApi';
import { priceChangeValidationSchema } from './validation';
import { buildBulkActionBasePayload } from '../../utils';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  categories: Category[];
  menuId: string;
  onClearSelection: () => void;
  onClose: () => void;
  selectedIds: Set<string>;
};

const Root = styled(WoltModal)`
  width: calc(100vw - 40px);
  min-width: 320px;
  max-width: 640px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  padding: 0;
  border-radius: 8px;
`;

const Footer = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRadio = styled(Radio)`
  & + & {
    margin-block-start: 8px;
  }
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 124px;
`;

const InputsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const OperationSelect = styled(Select)`
  width: 128px;
  margin-inline-end: 8px;
`;

const StyledInputMonetary = styled(InputMonetary)`
  width: 160px;
`;
const StyledInputNumber = styled(InputNumber)`
  width: 160px;
`;

const HintText = styled(InputAlert).attrs({ variant: 'info' })`
  margin-block-start: 8px;
`;

const CancelButton = styled(Button).attrs({ variant: 'secondary', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'primary', size: 'small' })``;

export type PriceChangeFormValues = {
  type:
    | 'singlePrice'
    | 'changeByAmount'
    | 'changeByPercentage'
    | 'changeByDiscountAmount'
    | 'changeByDiscountPercentage';
  operation: 'addition' | 'deduction';
  singlePrice: number | string | undefined;
  changeAmount: number | string | undefined;
  changePercentage: number | string | undefined;
  applyDiscountAmount: number | string | undefined;
  applyDiscountPercentage: number | string | undefined;
};

const amountFieldLabels: { [key in PriceChangeFormValues['type']]: string } = {
  singlePrice: 'menu.bulkActions.priceChange.singlePrice',
  changeByAmount: 'menu.bulkActions.priceChange.changeByAmount',
  changeByPercentage: 'menu.bulkActions.priceChange.changeByPercentage',
  changeByDiscountAmount: 'menu.bulkActions.priceChange.changeByDiscountAmount',
  changeByDiscountPercentage: 'menu.bulkActions.priceChange.changeByDiscountPercentage',
};

const operationOptions: { label: string; value: PriceChangeFormValues['operation'] }[] = [
  { label: 'Addition (+)', value: 'addition' },
  { label: 'Deduction (-)', value: 'deduction' },
];

const initialValues: PriceChangeFormValues = {
  type: 'singlePrice',
  operation: 'addition',
  singlePrice: undefined,
  changeAmount: undefined,
  changePercentage: undefined,
  applyDiscountAmount: undefined,
  applyDiscountPercentage: undefined,
};

type MutationProps = { payload: BulkEditPayload; priceChangeType: PriceChangeFormValues['type'] };

const BulkPriceChangeModal = ({ categories, menuId, onClearSelection, onClose, selectedIds }: Props) => {
  const intl = useIntl();
  const { updateMenuData } = useMenuData();
  const { currency } = useContext(CurrencyContext);
  const trackChangePrice = useBulkChangePriceTracking([...selectedIds]);

  const validationContext = useMemo(() => {
    return { currency };
  }, [currency]);

  const handlePriceChange = useMutation(({ payload }: MutationProps) => bulkEditItems(menuId, payload), {
    onMutate: (variables: MutationProps) => {
      reportBulkAction(selectedIds.size, 'BULK_CHANGE_PRICES', { priceChangeType: variables.priceChangeType });
    },
    onSuccess: (data: MenuContainer, variables: MutationProps) => {
      updateMenuData({ actionType: 'updateMenuContainer', payload: data });
      onClearSelection();
      trackChangePrice(variables.payload);
      toast.success(<FormattedMessage id="menu.bulkActions.priceChange.successDescription" />);
    },
    onError: () => {
      toast.error(<FormattedMessage id="menu.bulkActions.priceChange.errorDescription" />, { duration: Infinity });
    },
    onSettled: () => {
      onClose();
    },
  });

  return (
    <Root isOpen onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <BulkActionModalTitle
          count={selectedIds.size}
          title={<FormattedMessage id="menu.bulkActions.priceChange.changePrice" />}
        />
      </ModalHeader>
      <Form<PriceChangeFormValues>
        initialValues={initialValues}
        onSubmit={formValues => {
          const validFormValues = priceChangeValidationSchema.validateSync(formValues);
          const payload: BulkEditPayload = {
            ...buildBulkActionBasePayload(categories, selectedIds),
            ...buildBulkActionUpdatePricePayload(validFormValues),
          };
          handlePriceChange.mutate({ payload, priceChangeType: formValues.type });
        }}
        validate={validateYupSchema(priceChangeValidationSchema, validationContext)}
      >
        {({ handleSubmit, hasValidationErrors, pristine, submitting, values }) => (
          <form onSubmit={handleSubmit}>
            <ModalContent style={{ padding: 0 }}>
              <FormRow label={intl.formatMessage({ id: 'menu.bulkActions.priceChange.type' })} horizontalPadding>
                <Field<PriceChangeFormValues['type']> name="type">
                  {({ input }) => (
                    <RadioWrapper>
                      <StyledRadio
                        checked={input.value === 'singlePrice'}
                        onChange={() => {
                          input.onChange('singlePrice');
                        }}
                      >
                        <FormattedMessage id="menu.bulkActions.priceChange.singlePriceForAllItems" />
                      </StyledRadio>
                      <StyledRadio
                        checked={input.value === 'changeByAmount'}
                        onChange={() => {
                          input.onChange('changeByAmount');
                        }}
                      >
                        <FormattedMessage id="menu.bulkActions.priceChange.changePriceByAmount" />
                      </StyledRadio>
                      <StyledRadio
                        checked={input.value === 'changeByPercentage'}
                        onChange={() => {
                          input.onChange('changeByPercentage');
                        }}
                      >
                        <FormattedMessage id="menu.bulkActions.priceChange.changePriceByPercentage" />
                      </StyledRadio>
                      <StyledRadio
                        checked={input.value === 'changeByDiscountAmount'}
                        onChange={() => {
                          input.onChange('changeByDiscountAmount');
                        }}
                      >
                        <FormattedMessage id="menu.bulkActions.priceChange.changePriceByDiscountAmount" />
                      </StyledRadio>
                      <StyledRadio
                        checked={input.value === 'changeByDiscountPercentage'}
                        onChange={() => {
                          input.onChange('changeByDiscountPercentage');
                        }}
                      >
                        <FormattedMessage id="menu.bulkActions.priceChange.changePriceByDiscountPercentage" />
                      </StyledRadio>
                    </RadioWrapper>
                  )}
                </Field>
              </FormRow>
              <FormRow label={intl.formatMessage({ id: amountFieldLabels[values.type] })} horizontalPadding>
                <FieldWrapper>
                  <Field<PriceChangeFormValues['operation']> name="operation">
                    {({ input: operationInput }) => (
                      <>
                        <Field<PriceChangeFormValues['singlePrice']> name="singlePrice">
                          {field =>
                            values.type === 'singlePrice' && (
                              <RFFFieldErrorWrapper field={field}>
                                {input => (
                                  <InputsWrapper>
                                    <StyledInputMonetary {...input} currency={currency} />
                                  </InputsWrapper>
                                )}
                              </RFFFieldErrorWrapper>
                            )
                          }
                        </Field>
                        <Field<PriceChangeFormValues['changeAmount']> name="changeAmount">
                          {field =>
                            values.type === 'changeByAmount' && (
                              <RFFFieldErrorWrapper field={field}>
                                {input => (
                                  <InputsWrapper>
                                    <OperationSelect {...operationInput} options={operationOptions} />
                                    <StyledInputMonetary {...input} currency={currency} />
                                  </InputsWrapper>
                                )}
                              </RFFFieldErrorWrapper>
                            )
                          }
                        </Field>
                        <Field<PriceChangeFormValues['changePercentage']> name="changePercentage">
                          {field =>
                            values.type === 'changeByPercentage' && (
                              <RFFFieldErrorWrapper field={field}>
                                {input => (
                                  <InputsWrapper>
                                    <OperationSelect {...operationInput} options={operationOptions} />
                                    <StyledInputNumber {...input} suffixText="%" />
                                  </InputsWrapper>
                                )}
                              </RFFFieldErrorWrapper>
                            )
                          }
                        </Field>
                        <Field<PriceChangeFormValues['applyDiscountAmount']> name="applyDiscountAmount">
                          {field =>
                            values.type === 'changeByDiscountAmount' && (
                              <RFFFieldErrorWrapper field={field}>
                                {input => (
                                  <InputsWrapper>
                                    <StyledInputMonetary {...input} currency={currency} />
                                  </InputsWrapper>
                                )}
                              </RFFFieldErrorWrapper>
                            )
                          }
                        </Field>
                        <Field<PriceChangeFormValues['applyDiscountPercentage']> name="applyDiscountPercentage">
                          {field =>
                            values.type === 'changeByDiscountPercentage' && (
                              <RFFFieldErrorWrapper field={field}>
                                {input => (
                                  <InputsWrapper>
                                    <StyledInputNumber {...input} suffixText="%" />
                                  </InputsWrapper>
                                )}
                              </RFFFieldErrorWrapper>
                            )
                          }
                        </Field>
                      </>
                    )}
                  </Field>

                  <HintText>
                    <AmountFieldHint type={values.type} operation={values.operation} />
                  </HintText>
                </FieldWrapper>
              </FormRow>
            </ModalContent>
            <Footer>
              <CancelButton onClick={onClose}>
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SubmitButton
                type="submit"
                onClick={handleSubmit}
                disabled={pristine || submitting || hasValidationErrors}
                loading={handlePriceChange.isLoading}
              >
                <FormattedMessage id="state.save" />
              </SubmitButton>
            </Footer>
          </form>
        )}
      </Form>
    </Root>
  );
};

const AmountFieldHint = ({
  type,
  operation,
}: {
  type: PriceChangeFormValues['type'];
  operation: PriceChangeFormValues['operation'];
}) => {
  switch (type) {
    case 'singlePrice':
      return <FormattedMessage id="menu.bulkActions.priceChange.hints.singlePrice" />;
    case 'changeByAmount':
      if (operation === 'addition') {
        return <FormattedMessage id="menu.bulkActions.priceChange.hints.byAmountAddition" />;
      } else {
        return <FormattedMessage id="menu.bulkActions.priceChange.hints.byAmountDeduction" />;
      }
    case 'changeByPercentage':
      if (operation === 'addition') {
        return <FormattedMessage id="menu.bulkActions.priceChange.hints.byPercentageAddition" />;
      } else {
        return <FormattedMessage id="menu.bulkActions.priceChange.hints.byPercentageDeduction" />;
      }
    case 'changeByDiscountAmount':
      return <FormattedMessage id="menu.bulkActions.priceChange.hints.applyDiscountAmount" />;
    case 'changeByDiscountPercentage':
      return <FormattedMessage id="menu.bulkActions.priceChange.hints.applyDiscountPercentage" />;
  }
};

export default BulkPriceChangeModal;
